import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics, AnalyticsEvent, ApplicationInsightsEvent } from '../services/analytics';
import { getPageAnalyticsContext } from '../services/analyticsContext';

/**
 * Track each SPA route once at the application root. Product components use
 * `useAnalytics` without creating duplicate page views.
 */
export const usePageViewTracking = (enabled = true) => {
  const location = useLocation();

  useEffect(() => {
    if (!enabled) return;
    const frame = window.requestAnimationFrame(() => {
      analytics.trackPageView({
        page_title: document.title || 'Event Finder',
        page_location: window.location.href,
        page_path: location.pathname + location.search,
        custom_parameters: getPageAnalyticsContext(location.pathname, location.search),
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [enabled, location.pathname, location.search]);
};

/** Hook exposing explicit product analytics actions. */
export const useAnalytics = () => {
  return {
    // Track custom events
    trackEvent: (event: AnalyticsEvent) => analytics.trackEvent(event),
    trackApplicationInsightsEvent: (event: ApplicationInsightsEvent) =>
      analytics.trackApplicationInsightsEvent(event),
    
    // Track user interactions
    trackInteraction: (element: string, action: string, properties?: Record<string, any>) =>
      analytics.trackInteraction(element, action, properties),
    
    // Track search events
    trackSearch: (searchTerm: string, resultsCount?: number, filters?: Record<string, any>) =>
      analytics.trackSearch(searchTerm, resultsCount, filters),
    
    // Track event views
    trackEventView: (eventId: string, eventTitle: string, properties?: Record<string, any>) =>
      analytics.trackEventView(eventId, eventTitle, properties),
    
    // Track errors
    trackError: (error: Error, context?: string) => analytics.trackError(error, context),
    
    // Set user properties
    setUserProperties: (properties: Record<string, any>) => analytics.setUserProperties(properties),
    
    // Get analytics status
    getAnalyticsStatus: () => analytics.getStatus(),
  };
};

/**
 * HOC to wrap components with analytics
 */
export function withAnalytics<T extends Record<string, any>>(
  Component: React.ComponentType<T>
): React.ComponentType<T> {
  return function AnalyticsWrappedComponent(props: T) {
    useAnalytics(); // This will handle automatic page view tracking
    return <Component {...props} />;
  };
}

export default useAnalytics;